import React, { useEffect } from "react";
import { Button } from "@clarkinc/zest-design-system";
import { User } from "~/common/Store/models/User";
import {
  AutoReorder,
  UserProductPurchasingData,
} from "~/ProductDetails/ProductDetailPageResult";
import ReorderOptionGroup from "./ReorderOptionGroup";
import ReorderToggle from "./ReorderOptionGroup/ReorderToggle";
import {
  sendGtmEvent,
  clearGa4EventInDataLayer,
} from "../../../../analytics/definitions";
import loginStore from "~/common/LoginModalV2/loginModalStore";

interface AutoReorderProps {
  itemNumber: string;
  hasFreeShipping: boolean;
  isWebstaurantPlusEligible: boolean;
  user: User;
  userProductPurchasingData: UserProductPurchasingData;
  autoReorder: AutoReorder;
  onChange: (autoReorderData: AutoReorder) => void;
  isInStock: boolean;
  mounted: boolean;
}

const AutoReorder = ({
  itemNumber,
  hasFreeShipping,
  isWebstaurantPlusEligible,
  user,
  userProductPurchasingData,
  autoReorder,
  onChange,
  isInStock,
  mounted,
}: AutoReorderProps) => {
  const handleNewAutoReorderIntervalChange = ({
    target,
  }: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
    const selectedLabel =
      target.querySelector(`[value='${target.value}']`)?.textContent ?? "";

    onChange({
      ...autoReorder,
      newAutoReorderInterval: target.value,
      autoReorderDropdownValue: selectedLabel,
    });

    clearGa4EventInDataLayer();
    sendGtmEvent({
      event: "ga4_event",
      eventName: "PDP Auto Reorder",
      eventAction: "Auto Reorder Dropdown - New",
      eventLabel: selectedLabel,
    });
  };

  const handleSetExistingAutoReorderInterval = ({
    target,
  }: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
    const selectedAutoReorder =
      userProductPurchasingData.activeAutoReorders.find(
        (x) => x.id.toString() === target.value,
      );

    const selectedLabel =
      target.querySelector(`[value='${target.value}']`)?.textContent ?? "";

    if (selectedAutoReorder) {
      onChange({
        ...autoReorder,
        selectedExistingAutoReorder: selectedAutoReorder,
        autoReorderDropdownValue: selectedLabel,
      });

      clearGa4EventInDataLayer();
      sendGtmEvent({
        event: "ga4_event",
        eventName: "PDP Auto Reorder",
        eventAction: "Auto Reorder Dropdown - Existing",
        eventLabel: selectedLabel,
      });
    }
  };

  const handleRadioSelectChange = () => {
    onChange({
      ...autoReorder,
      isAddToExistingAutoReorder: !autoReorder.isAddToExistingAutoReorder,
    });
  };

  const handleAutoReorderEnableChange = () => {
    onChange({
      ...autoReorder,
      isToggleEnabled: !autoReorder.isToggleEnabled,
    });

    clearGa4EventInDataLayer();
    sendGtmEvent({
      event: "ga4_event",
      eventName: "PDP Auto Reorder",
      eventAction: "Auto Reorder Toggle",
      eventLabel: itemNumber,
    });
  };

  const hasExistingAutoReorder =
    userProductPurchasingData.activeAutoReorders?.length > 0;

  const getDescriptionText = () => {
    const defaultText =
      "Get 25% off shipping every time this item ships automatically.";
    const freeShippingText =
      "Have this item shipped automatically for added convenience.";

    return hasFreeShipping || (user.hasWebPlus && isWebstaurantPlusEligible)
      ? freeShippingText
      : defaultText;
  };

  useEffect(() => {
    if (user.isLoggedIn) {
      loginStore.closeLogin();
    }
  }, [user]);

  const handleSigninClick = () => {
    const loginModalCallback = () => {
      onChange({
        ...autoReorder,
        isToggleEnabled: true,
      });
    };

    loginStore.openLogin({
      callback: loginModalCallback,
      targetUrl: window.location.pathname,
      queryParams: window.location.search,
    });
  };

  return (
    <div
      className={`toggle mb-[10px] pr-0 transition-all overflow-hidden ${
        autoReorder.isToggleEnabled ? `toggle--active` : ""
      }`}
      data-testid="AutoReorder"
    >
      <div className={`toggle__group ${autoReorder.isToggleEnabled && `pb-2`}`}>
        <i className="toggle__icon icon-history pr-3" />
        <div className="toggle__subgroup">
          <p className="toggle__heading header-4 leading-[18px]">
            Auto Reorder
          </p>
          {mounted ? (
            <p className="toggle__text leading-3" id="auto-reorder-description">
              {getDescriptionText()}
            </p>
          ) : (
            <div className="toggle__skeleton" />
          )}
        </div>
        <div
          className={`whitespace-nowrap ${user.registered ? "" : "mr-2"} ${
            mounted ? "" : "mr-[73px]"
          } ${user.hasWebPlus ? "ml-0 xl:ml-5" : "ml-5 xl:ml-10"}`}
        >
          {mounted &&
            (user.registered ? (
              <div
                className="toggle__switch onoffswitch mr-[2px] ml-[21px]"
                data-testid="onOffSwitch"
              >
                <ReorderToggle
                  disabled={!isInStock}
                  onToggle={() => handleAutoReorderEnableChange()}
                  pressed={autoReorder.isToggleEnabled}
                />
              </div>
            ) : (
              <Button
                size="small"
                theme="wss"
                type="button"
                variant="white"
                className="cursor-pointer w-[65px]"
                onClick={handleSigninClick}
              >
                Sign in
              </Button>
            ))}
        </div>
      </div>

      {isInStock && (
        <form
          className="pb-3"
          data-testid="autoReorderOptionsContainer"
          hidden={!autoReorder.isToggleEnabled}
        >
          <fieldset className="border-none p-0">
            <legend className="sr-only">
              Create New Auto Reorder Interval or Choose Existing Auto Reorder
            </legend>
            <ReorderOptionGroup
              selectAriaLabel="Add New Auto Reorder Interval"
              inputTestId="newAutoReorderRadio"
              selectTestId="intervalDropdown"
              onRadioChange={handleRadioSelectChange}
              onSelectChange={handleNewAutoReorderIntervalChange}
              selectOptions={[
                {
                  value: "0",
                  label: "Create New Auto Reorder",
                  disabled: true,
                },
                { value: "5", label: "Ship every 2 Weeks" },
                { value: "1", label: "Ship every 1 Month" },
                { value: "2", label: "Ship every 2 Months" },
                { value: "3", label: "Ship every 3 Months" },
                { value: "4", label: "Ship every 6 Months" },
              ]}
              defaultChecked
            />
            {hasExistingAutoReorder && (
              <ReorderOptionGroup
                inputTestId="existingAutoReorderRadio"
                selectTestId="existingAutoReorderDropdown"
                onRadioChange={handleRadioSelectChange}
                onSelectChange={handleSetExistingAutoReorderInterval}
                selectAriaLabel="Choose an Existing Auto Reorder"
                selectOptions={[
                  {
                    disabled: true,
                    value: "0",
                    label: "Add To Existing Auto Reorder",
                  },
                  ...userProductPurchasingData.activeAutoReorders
                    .filter((reorder) => reorder.isActive)
                    .map((reorder) => ({
                      value: reorder.id.toString(),
                      label:
                        reorder.name.length > 0
                          ? reorder.name
                          : `Auto Reorder #${reorder.id}`,
                    })),
                ]}
              />
            )}
          </fieldset>
        </form>
      )}
    </div>
  );
};

export default AutoReorder;
